import { useEffect } from 'react';
import { messaging, getToken, onMessage, VAPID_KEY, isConfigured } from '@/lib/firebase';
import client from '@/api/client';

/**
 * Requests browser notification permission, registers the FCM token with the
 * backend, and wires up foreground message handling.
 *
 * Call this once — inside AppShell after the user is authenticated.
 */
export function usePushNotifications(
  onForegroundMessage: (title: string, body: string) => void
) {
  useEffect(() => {
    if (!isConfigured || !messaging) return;

    async function setup() {
      try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;

        const token = await getToken(messaging!, { vapidKey: VAPID_KEY });
        if (!token) return;

        // Save the token to the backend so the server can send push notifications
        await client.post('/notifications/fcm-token', { token });

        // Handle messages received while the app is open (foreground)
        onMessage(messaging!, (payload) => {
          const title = payload.notification?.title ?? payload.data?.title ?? 'PG Manager';
          const body  = payload.notification?.body  ?? payload.data?.body  ?? '';
          onForegroundMessage(title, body);
        });
      } catch (err) {
        // Notifications blocked or Firebase not yet configured — fail silently
        console.debug('[FCM] Push notification setup skipped:', err);
      }
    }

    setup();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
